// ============================================================================
// PatientTestsTab — «Тесты и результаты» кабинета пациента.
//
// Два источника (SSoT — сервер):
//   • psy-назначения (psy_assignments + типы опросов): активные назначения со
//     ссылкой/коротким кодом, результаты и история — как в рабочем месте
//     психолога, но БЕЗ выдачи ссылок и ручных результатов;
//   • общий кабинет опросов (модуль psy не установлен) — доступные методики и
//     СВОИ протоколы; прохождение — существующим FillRunner.
//
// Нет права на назначения/протоколы — явное ограничение (права SSoT сервера),
// а не пустой список. Прохождение теста — единственное действие пациента.
// ============================================================================

import { useMemo, useState } from 'react';
import { ClipboardList, ExternalLink, FlaskConical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FillRunner } from '@/components/polls/FillRunner2';
import { AssignmentCard } from '@/components/psy/PsyPatientCabinet';
import { PsyResultsHistory } from '@/components/psy/PsyResultsHistory';
import { InlineNotice, SectionState } from '@/components/medic/PatientCardStates';
import { conclusionsOf, type PatientTest } from '@/lib/patientCabinet';
import { buildCabinetRows, type PsyCabinetData } from '@/lib/psyWorkspace';
import { openPollCodeEntry, openPollLink } from '@/lib/pollGuestLinks';
import type { CabinetTests } from '@/lib/patientCabinetDashboard';
import { formatValue } from '@/lib/format';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';

interface Props {
    data: CabinetTests;
    onRefresh: () => void;
}

function stateTitle(state: CabinetTests['state']): string {
    if (state === 'denied') return 'Тесты и результаты недоступны';
    if (state === 'unlinked') return 'Нет связанной карточки';
    if (state === 'ambiguous') return 'Требуется уточнение карточки';
    if (state === 'unavailable') return 'Тесты в базе не найдены';
    if (state === 'error') return 'Не удалось загрузить тесты';
    return 'Тесты и результаты';
}

function stateTone(state: CabinetTests['state']): 'muted' | 'warning' | 'danger' {
    if (state === 'error') return 'danger';
    if (state === 'denied' || state === 'ambiguous') return 'warning';
    return 'muted';
}

/** Общий кабинет опросов (без psy): доступные тесты + FillRunner. */
function PollTests({ poll, onRefresh }: {
    poll: NonNullable<CabinetTests['poll']>;
    onRefresh: () => void;
}) {
    const showError = useToastStore((s) => s.showError);
    const showSuccess = useToastStore((s) => s.showSuccess);
    const [filling, setFilling] = useState<PatientTest | null>(null);
    const [fillValues, setFillValues] = useState<Record<string, unknown>>({});
    const [lastResult, setLastResult] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const startFill = (test: PatientTest) => {
        if (!test.templateId) {
            showError('К методике не привязан шаблон вопросов — обратитесь к специалисту');
            return;
        }
        setFillValues({});
        setLastResult('');
        setFilling(test);
    };

    const submit = async () => {
        if (!filling) return;
        setSubmitting(true);
        try {
            const answers = Object.entries(fillValues).map(([questionId, value]) => ({
                questionId,
                valueJson: JSON.stringify(value ?? ''),
            }));
            const result = await getSDK().poll.submitPollAnswers({
                pollId: filling.templateId,
                subjectId: '',
                attempt: 0,
                answers,
            });
            const parts = conclusionsOf(result.conclusionsJson);
            if (result.passed) parts.unshift('Пройдено');
            if (result.percent > 0) parts.push(`Результат: ${Math.round(result.percent)}%`);
            setLastResult(parts.join(' · ') || 'Результат сохранён');
            setFilling(null);
            setFillValues({});
            showSuccess('Анкета отправлена');
            onRefresh();
        } catch (e) {
            logErrorToJournal(e, `Кабинет пациента: отправка «${filling.synonym}»`);
            showError(friendlyError(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="space-y-4">
            <section>
                <h3 className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">Доступные тесты</h3>
                {poll.tests.length === 0 && (
                    <div className="rounded-lg border bg-card px-3 py-2 text-xs text-muted-foreground">
                        Назначенных тестов нет — специалист назначит методику, она появится здесь.
                    </div>
                )}
                <div className="grid gap-2 sm:grid-cols-2">
                    {poll.tests.map((test) => (
                        <div key={test.code} className="flex flex-col gap-2 rounded-lg border bg-card p-3">
                            <div className="text-sm font-medium">{test.synonym}</div>
                            {test.description && <div className="text-xs text-muted-foreground">{test.description}</div>}
                            <Button size="sm" className="mt-auto h-7 self-start text-[11px]" onClick={() => startFill(test)}>
                                Пройти
                            </Button>
                        </div>
                    ))}
                </div>
            </section>

            <section>
                <h3 className="mb-2 flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <ClipboardList className="h-3 w-3" />Мои результаты
                </h3>
                {lastResult && (
                    <div className="mb-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">{lastResult}</div>
                )}
                {poll.protocolsDenied ? (
                    <div className="rounded-lg border bg-card px-3 py-2 text-xs text-muted-foreground">
                        Просмотр результатов не разрешён роли — результаты ведёт и интерпретирует специалист.
                    </div>
                ) : poll.protocols.length === 0 ? (
                    <div className="rounded-lg border bg-card px-3 py-2 text-xs text-muted-foreground">
                        Результатов пока нет — пройдите назначенный тест.
                    </div>
                ) : (
                    <div className="overflow-auto rounded-lg border">
                        <table className="w-full border-collapse text-xs">
                            <thead>
                                <tr className="border-b bg-muted/40 text-left text-muted-foreground">
                                    <th className="whitespace-nowrap px-2 py-1.5 font-medium">Дата</th>
                                    <th className="px-2 py-1.5 font-medium">Тест</th>
                                    <th className="whitespace-nowrap px-2 py-1.5 font-medium">Балл</th>
                                    <th className="whitespace-nowrap px-2 py-1.5 font-medium">Пройдено</th>
                                    <th className="px-2 py-1.5 font-medium">Заключение</th>
                                </tr>
                            </thead>
                            <tbody>
                                {poll.protocols.map((protocol) => (
                                    <tr key={protocol.id} className="border-b last:border-0">
                                        <td className="whitespace-nowrap px-2 py-1.5 align-top">
                                            {protocol.createdAt ? formatValue(protocol.createdAt, '', 'timestamp') : ''}
                                        </td>
                                        <td className="px-2 py-1.5 align-top">{protocol.testName}</td>
                                        <td className="px-2 py-1.5 align-top">{protocol.score}</td>
                                        <td className="px-2 py-1.5 align-top">{protocol.passed ? '✅' : '—'}</td>
                                        <td className="max-w-[320px] break-words px-2 py-1.5 align-top">{protocol.conclusion}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {filling && (
                <FillRunner
                    poll={{ id: filling.templateId, name: filling.name, synonym: filling.synonym }}
                    values={fillValues}
                    setValues={setFillValues}
                    onCancel={() => { setFilling(null); setFillValues({}); }}
                    onSubmit={() => { if (!submitting) void submit(); }}
                />
            )}
        </div>
    );
}

/** Кабинет с назначениями психолога: ссылки/коды и результаты. */
function PsyTests({ data, onRefresh }: Props) {
    const showError = useToastStore((s) => s.showError);
    const openLink = (token: string) => {
        void openPollLink(token).catch((e) => {
            logErrorToJournal(e, 'Кабинет пациента: ссылка прохождения теста');
            showError(friendlyError(e));
        });
    };
    const openCode = () => {
        void openPollCodeEntry().catch((e) => {
            logErrorToJournal(e, 'Кабинет пациента: страница ввода кода');
            showError(friendlyError(e));
        });
    };
    const cabinet: PsyCabinetData = useMemo(() => ({
        methods: data.methods,
        assignments: data.assignments,
        results: data.results,
        resultsDenied: data.resultsState === 'denied',
    }), [data]);
    const rows = useMemo(() => buildCabinetRows(cabinet), [cabinet]);
    const methodName = useMemo(() => {
        const map = new Map(data.methods.map((method) => [method.code, method.synonym]));
        return (code: string) => map.get(code) || code;
    }, [data.methods]);

    return (
        <div className="space-y-4">
            <section>
                <h3 className="mb-2 flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <FlaskConical className="h-3 w-3" />Назначенные тесты
                </h3>
                {data.assignmentsState !== 'ready' && (
                    <InlineNotice
                        text={data.assignmentsError || 'Назначения недоступны.'}
                        tone={data.assignmentsState === 'error' ? 'danger' : 'warning'}
                        onRetry={onRefresh}
                    />
                )}
                {data.assignmentsState === 'ready' && rows.active.length === 0 && (
                    <div className="rounded-lg border bg-card px-3 py-2 text-xs text-muted-foreground">
                        Активных назначений нет. Специалист назначит методику — она появится здесь со ссылкой и кодом.
                    </div>
                )}
                {data.assignmentsState === 'ready' && rows.active.length > 0 && (
                    <div className="grid gap-2 lg:grid-cols-2">
                        {rows.active.map((row) => (
                            <AssignmentCard
                                key={row.assignment.id}
                                row={row}
                                canManage={false}
                                onOpenLink={(link) => openLink(link.token)}
                                onEnterCode={openCode}
                                onManage={() => undefined}
                            />
                        ))}
                    </div>
                )}
            </section>

            <section>
                <PsyResultsHistory
                    results={data.results}
                    assignments={data.assignmentsState === 'ready' ? data.assignments : undefined}
                    methodName={methodName}
                    denied={data.resultsState === 'denied'}
                    emptyText="Результатов пока нет — пройдите назначенный тест по ссылке или коду."
                />
                {data.resultsState === 'error' && (
                    <div className="mt-2">
                        <InlineNotice text={data.resultsError} tone="danger" onRetry={onRefresh} />
                    </div>
                )}
            </section>

            {rows.history.length > 0 && (
                <section>
                    <h3 className="mb-2 flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                        <ExternalLink className="h-3 w-3" />История назначений
                    </h3>
                    <div className="space-y-1.5">
                        {rows.history.map((row) => (
                            <div key={row.assignment.id} className="flex items-start gap-2 rounded-lg border bg-card px-3 py-2 text-xs">
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-sm font-medium">
                                        {row.method?.synonym || row.assignment.methodCode || 'Методика'}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground">
                                        {row.assignment.assignedDate ? formatValue(row.assignment.assignedDate, '', 'date') : '—'}
                                        {' · '}
                                        {row.assignment.status === 'assigned'
                                            ? 'Назначена'
                                            : row.assignment.status === 'done'
                                                ? 'Выполнена'
                                                : row.assignment.status === 'cancelled'
                                                    ? 'Отменена'
                                                    : row.assignment.status || 'Назначена'}
                                    </div>
                                </div>
                                <span className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] ${
                                    row.lastResult
                                        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                                        : 'border-border bg-muted/50 text-muted-foreground'
                                }`}>
                                    {row.lastResult ? 'есть результат' : 'результата нет'}
                                </span>
                            </div>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}

export function PatientTestsTab({ data, onRefresh }: Props) {
    if (data.state !== 'ready') {
        return (
            <SectionState
                title={stateTitle(data.state)}
                text={data.error || data.note}
                tone={stateTone(data.state)}
                onRetry={onRefresh}
            />
        );
    }
    if (data.kind === 'poll' && data.poll) {
        return <PollTests poll={data.poll} onRefresh={onRefresh} />;
    }
    if (data.kind === 'psy') {
        return <PsyTests data={data} onRefresh={onRefresh} />;
    }
    return (
        <SectionState
            title="Тесты недоступны"
            text={data.note || 'Не удалось определить источник тестов для вашей учётной записи.'}
            tone="warning"
            onRetry={onRefresh}
        />
    );
}
